/**
 * Shared helpers + local enum mirrors for the SEO board (route /seo).
 *
 * TODO: these string unions mirror `apps/aixinternal-api/src/constants/seoEnums.ts`
 * (ESeoPageState / ESeoPageHealth / ESeoKeywordStatus). They are duplicated here
 * because that file lives in the api app's src (not a shared `packages/*`), so a Vite
 * frontend cannot cleanly import its runtime values. If these enums ever move to
 * `packages/aixinternal-shared`, delete this mirror and import from there.
 */
import type { BadgeProps } from "@aix/ui"

/** VidMax is the active programmatic SEO product. */
export const SEO_DEFAULT_PRODUCT_KEY = "vidmax"

export const SEO_PAGE_STATES = ["draft", "gated", "published", "pruned"] as const

export const SEO_PAGE_HEALTHS = [
  "unknown",
  "indexing",
  "dead",
  "ctr-leak",
  "near-miss",
  "converting",
  "performing",
] as const

export const SEO_KEYWORD_STATUSES = ["draft", "approved", "rejected"] as const

type BadgeVariant = NonNullable<BadgeProps["variant"]>

export function stateBadgeVariant(state: string): BadgeVariant {
  switch (state) {
    case "published":
      return "success"
    case "gated":
      return "default"
    case "pruned":
      return "destructive"
    default:
      return "secondary"
  }
}

export function healthBadgeVariant(health: string): BadgeVariant {
  switch (health) {
    case "performing":
    case "converting":
      return "success"
    case "dead":
    case "ctr-leak":
      return "destructive"
    case "near-miss":
      return "default"
    default:
      return "secondary"
  }
}

export function keywordStatusBadgeVariant(status: string): BadgeVariant {
  switch (status) {
    case "approved":
      return "success"
    case "rejected":
      return "destructive"
    default:
      return "secondary"
  }
}

export function operationBadgeVariant(state: string): BadgeVariant {
  switch (state) {
    case "active":
    case "approved":
    case "succeeded":
      return "success"
    case "failed":
    case "cancelled":
    case "rolled-back":
      return "destructive"
    case "running":
    case "staged":
      return "default"
    default:
      return "secondary"
  }
}

export function runContextText(checkpoint: unknown, key: string): string | undefined {
  if (!checkpoint || typeof checkpoint !== "object" || !("context" in checkpoint)) return undefined
  const context = checkpoint.context
  if (!context || typeof context !== "object" || !(key in context)) return undefined
  const value = context[key as keyof typeof context]
  return typeof value === "string" ? value : undefined
}

/** Build the public live URL for a page from the product domain + slug. */
export function liveUrlFor(domain: string | null | undefined, slug: string): string {
  const host = (domain ?? "").replace(/^https?:\/\//, "").replace(/\/$/, "")
  const path = slug.startsWith("/") ? slug : `/${slug}`
  return host ? `https://${host}${path}` : path
}
